import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "@/components/layout/Layout";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import { JournalPost, SITE_URL, absoluteUrl, formatDate, getPublishedPost, listPublishedPosts } from "@/lib/journalApi";

const JournalArticle = () => {
  const { slug = "" } = useParams();
  const [post, setPost] = useState<JournalPost | null | undefined>(undefined);
  const [related, setRelated] = useState<JournalPost[]>([]);

  useEffect(() => {
    setPost(undefined);
    getPublishedPost(slug).then(setPost).catch(() => setPost(null));
    listPublishedPosts()
      .then((all) => {
        const others = all.filter((p) => p.slug !== slug);
        const current = all.find((p) => p.slug === slug);
        const same = others.filter((p) => p.category === current?.category);
        setRelated([...same, ...others.filter((p) => !same.includes(p))].slice(0, 3));
      })
      .catch(() => {});
  }, [slug]);

  if (post === undefined) {
    return <Layout><div className="min-h-screen bg-background pt-40 text-center text-foreground/50">Loading…</div></Layout>;
  }
  if (post === null) {
    return (
      <Layout>
        <Helmet><title>Article not found | Olivia Flemming</title><meta name="robots" content="noindex" /></Helmet>
        <div className="min-h-screen bg-background pt-40 px-5 text-center">
          <h1 className="font-playfair text-3xl text-foreground">This article is not available</h1>
          <Link to="/journal" className="inline-block mt-6 text-gold underline underline-offset-4">Back to the Journal</Link>
        </div>
      </Layout>
    );
  }

  const url = `${SITE_URL}/journal/${post.slug}`;
  const title = post.seo_title || post.title;
  const description = post.seo_description || post.excerpt;
  const image = absoluteUrl(post.cover_image_url);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description,
    image: image ? [image] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Person", name: post.author, url: SITE_URL },
    publisher: { "@type": "Person", name: "Olivia Flemming", url: SITE_URL },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: post.category,
  };

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {image && <meta property="og:image" content={image} />}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {image && <meta name="twitter:image" content={image} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article className="bg-background">
        <header className="pt-32 md:pt-40 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto px-5 text-center">
            <nav aria-label="Breadcrumb" className="text-xs tracking-[0.25em] uppercase text-foreground/45 mb-6">
              <Link to="/journal" className="hover:text-gold">Journal</Link>
              <span className="mx-2 text-gold/60">·</span>
              <span className="text-gold">{post.category}</span>
            </nav>
            <h1 className="font-playfair text-3xl md:text-5xl leading-tight text-foreground">{post.title}</h1>
            <div className="w-12 h-px bg-gold/70 mx-auto my-6" />
            <p className="font-playfair italic text-lg md:text-xl text-foreground/75 leading-relaxed">{post.excerpt}</p>
            <p className="text-foreground/45 text-sm mt-6">
              By {post.author} · <time dateTime={post.published_at ?? undefined}>{formatDate(post.published_at)}</time>
            </p>
          </div>
        </header>

        {post.cover_image_url && (
          <div className="max-w-5xl mx-auto px-5 mb-12 md:mb-16">
            <img src={post.cover_image_url} alt={post.cover_alt ?? post.title} className="w-full aspect-[16/9] object-cover border border-gold/20" />
          </div>
        )}

        <div className="max-w-2xl mx-auto px-5 pb-16 journal-prose">
          <ReactMarkdown
            components={{
              a: ({ href = "", children }) =>
                href.startsWith("/") ? <Link to={href}>{children}</Link> : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
            }}
          >
            {post.body}
          </ReactMarkdown>

          <div className="mt-14 border border-gold/30 bg-cherry-deep/40 p-8 text-center">
            <p className="font-playfair text-2xl text-foreground">Planning an occasion?</p>
            <p className="text-foreground/65 mt-2">Message Olivia to check availability.</p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 rounded-full bg-cherry border border-gold/50 px-6 py-3 text-xs uppercase tracking-[0.2em] text-foreground hover:bg-cherry/80 transition-colors">
              <FaWhatsapp className="w-4 h-4" /> Message me
            </a>
          </div>
        </div>

        {related.length > 0 && (
          <section className="border-t border-gold/15 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-5">
              <h2 className="font-playfair text-2xl md:text-3xl text-center text-foreground mb-10">More from the Journal</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {related.map((p) => (
                  <Link key={p.id} to={`/journal/${p.slug}`} className="group block">
                    {p.cover_image_url && (
                      <div className="aspect-[4/3] overflow-hidden border border-gold/20 mb-4">
                        <img src={p.cover_image_url} alt={p.cover_alt ?? p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <p className="text-gold text-[11px] tracking-[0.3em] uppercase mb-2">{p.category}</p>
                    <h3 className="font-playfair text-lg text-foreground group-hover:text-gold transition-colors">{p.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
};

export default JournalArticle;
